import React, { useState, useRef } from "react"
import {
    Box,
    TextField,
    Button,
    Typography,
    Stack,
    IconButton,
} from "@mui/material"
import Header from "../../Layouts/AppHeader/AppHeader"
import { sha256 } from "js-sha256"
import JSEncrypt from "jsencrypt"
import { MobileOtpRequestApi } from "../../../api/OtpVerification/MobileOtpVerification"
import { MobileOtpResponse } from "../../../@types/DRO/MobileOtpResponse"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faEdit } from "@fortawesome/free-solid-svg-icons"
import { FormDatasSubmission } from "../../../api/FormData/FormDataSubmissionApi"

const MobileOtpVerification: React.FC = () => {
    const [mobileNumber, setMobileNumber] = useState("")
    const [otp, setOtp] = useState(["", "", "", ""])
    const [otpSent, setOtpSent] = useState(false)
    const [verificationMessage, setVerificationMessage] = useState("")
    const [loader, setLoader] = useState<boolean>(false)
    const otpRefs = useRef<(HTMLInputElement | null)[]>([])
    const [isVerified, setIsVerified] = useState(false)
    const [apiError, setApiError] = useState<string | null>(null)
    const [verifyApiError, setVerifyApiError] = useState<string | null>(null)
    const [isEditable, setIsEditable] = useState(true)
    const [fetchedData, setFetchedData] = useState<any | null>(null) // New state for fetched data
    const hexToBytes = (hex: string) => {
        const bytes: number[] = []
        for (let i = 0; i < hex.length; i += 2) {
            bytes.push(parseInt(hex.substr(i, 2), 16))
        }
        return bytes
    }

    const navigate = useNavigate()
    const PUBLIC_KEY =
        "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAg79k2m8PlGNIyJum2uzGqnhdjNqc90SF5FwoOlU7I6Hy6xh36K2qzw1Q5wLcfM+KojFy0xLzKf/u03Wm9S/SmbwvzlXaCq5uYEtVqJBxCz3Y+ZyTRlWyWDJki+OvGYqGbw1EmmKFt2SaOjsE/e+lo6KQpFZwGuaJ1ZKQjCbKRdmkOUAWIjK51a3r8kTnq7XIMf9iXaHX5qvTS/BMcRrXTQ5/JGeuy9OCaJ0S7YrSINZymd0puA0IH87QxDmnUoq+pJwo8l67MFwK7rLyCDSRGwdVY4kLQw8T25k53eoH6+ZUMLydkR4X/ryXYERIw/OwPWpoioHARKzyA/ARA2jkTQIDAQAB"

    const handleRequestOtp = async () => {
        setLoader(true)
        setApiError(null)
        try {
            const timestamp = String(Date.now())
            const hash = sha256(mobileNumber + timestamp)
            const hashBytes = hexToBytes(hash)
            const hashBase64 = btoa(String.fromCharCode(...hashBytes))
            const data = `${mobileNumber}:${timestamp}:${hashBase64}`
            const encrypt = new JSEncrypt()
            encrypt.setPublicKey(PUBLIC_KEY)

            const encryptedData = encrypt.encrypt(data)
            if (!encryptedData)
                throw new Error("Encryption failed. Invalid data.")

            const response = await MobileOtpRequestApi.api.MobileOtpRequest(
                encryptedData
            )
            if (response) {
                setOtpSent(true)
                setIsEditable(false) // Disable fields after OTP is sent
            }
            setLoader(false)
        } catch (error: any) {
            console.error("Error requesting OTP:", error)
            setApiError(
                error.response?.data.message ||
                    "Failed to request OTP. Please try again."
            )
            setLoader(false)
        }
    }

    const generateEncryptedData = (input: string) => {
        const timestamp = String(Date.now())
        const hash = sha256(input + timestamp)
        const hashBytes = hexToBytes(hash)
        const hashBase64 = btoa(String.fromCharCode(...hashBytes))
        return `${input}:${timestamp}:${hashBase64}`
    }

    const encryptData = (data: string) => {
        const encrypt = new JSEncrypt()
        encrypt.setPublicKey(PUBLIC_KEY)
        return encrypt.encrypt(data)
    }

    const redirectToCaptureImage = (data: any) =>
        navigate("/capture-image", { state: { mobileNumber, fetchedData:data } })

    const handleVerifyOtp = async () => {
        setVerifyApiError(null) // Reset API error before making a new request
        try {
            const data = generateEncryptedData(mobileNumber)
            const encryptedPhoneNumber = encryptData(data)
            if (!encryptedPhoneNumber) throw new Error("Encryption failed.")

            const response = await MobileOtpRequestApi.api.verifyOtp(
                encryptedPhoneNumber,
                otp.join("")
            )
            if (response.message) {
                setIsVerified(true)
                setVerificationMessage("OTP verified successfully")

                // Fetch data if OTP is verified
                const fetchedDataResponse =
                    await FormDatasSubmission.api.fetchDataFromMobileOtp(
                        mobileNumber
                    )
                if (fetchedDataResponse) {
                    setFetchedData(fetchedDataResponse.data)
                    redirectToCaptureImage(fetchedDataResponse.data)
                } else {
                    redirectToCaptureImage(null)
                }
            } else {
                setVerificationMessage(
                    "OTP verification failed. Please try again."
                )
            }
        } catch (error: any) {
            console.error("Error verifying OTP:", error)
            setVerifyApiError(
                error.response?.data.message ||
                    "Failed to verify OTP. Please try again."
            )
            setVerificationMessage("OTP verification failed. Please try again.")
        }
    }

    const handleOtpChange = (index: number, value: string) => {
        if (/^\d*$/.test(value)) {
            const newOtp = [...otp]
            newOtp[index] = value
            setOtp(newOtp)

            // Move focus to the next input field when a digit is entered
            if (value && index < otpRefs.current.length - 1) {
                otpRefs.current[index + 1]?.focus()
            }

            // Move focus to the previous input field when a digit is deleted
            if (!value && index > 0) {
                otpRefs.current[index - 1]?.focus()
            }
        }
    }

    const handleMobileNumberChange = (value: string) => {
        if (/^\d*$/.test(value) && value.length <= 10) {
            setMobileNumber(value)
        }
    }

    const handleBackClick = () => {
        navigate(-1) // Navigate back to the previous page
    }

    const handleEditClick = () => {
        setIsEditable(true)
        setOtpSent(false)
    }

    return (
        <>
            <Header />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "80vh",
                    backgroundColor: "#ffffff",
                    padding: 2,
                    position: "relative", // Add relative positioning
                }}
            >
                <IconButton
                    onClick={handleBackClick}
                    sx={{ position: "absolute", top: 16, left: 16 }}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </IconButton>
                <Typography variant="h4" gutterBottom>
                    Mobile Verification
                </Typography>
                <Stack
                    spacing={2}
                    direction="column"
                    alignItems="center"
                    sx={{ width: "90%", maxWidth: 350 }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <TextField
                            label="Mobile Number"
                            variant="outlined"
                            value={mobileNumber}
                            onChange={(e) =>
                                handleMobileNumberChange(e.target.value)
                            }
                            sx={{ flex: 1 }}
                            inputProps={{ maxLength: 10, inputMode: "numeric" }}
                            disabled={!isEditable}
                        />
                        {otpSent && (
                            <IconButton
                                onClick={handleEditClick}
                                sx={{ ml: 1 }}
                            >
                                <FontAwesomeIcon icon={faEdit} />
                            </IconButton>
                        )}
                    </Box>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#3ea787",
                            width: "100%",
                            height: 40,
                        }}
                        onClick={handleRequestOtp}
                        disabled={mobileNumber.length !== 10 || !isEditable}
                    >
                        Send OTP
                    </Button>
                </Stack>
                {apiError && (
                    <Typography variant="body2" color="error" mt={2}>
                        {apiError}
                    </Typography>
                )}
                {otpSent && (
                    <>
                        <Typography variant="subtitle1" gutterBottom mt={2}>
                            Enter OTP below
                        </Typography>
                        <Stack
                            spacing={1}
                            direction="row"
                            alignItems="center"
                            mt={1}
                            sx={{ width: "90%", maxWidth: 350 }}
                        >
                            {otp.map((digit, index) => (
                                <TextField
                                    key={index}
                                    variant="outlined"
                                    value={digit}
                                    onChange={(e) =>
                                        handleOtpChange(index, e.target.value)
                                    }
                                    inputProps={{
                                        maxLength: 1,
                                        style: { textAlign: "center" },
                                        inputMode: "numeric", // Show numeric keyboard
                                    }}
                                    sx={{ flex: 1, height: 40 }}
                                    inputRef={(el) =>
                                        (otpRefs.current[index] = el)
                                    }
                                />
                            ))}
                        </Stack>
                        {verifyApiError && (
                            <Typography variant="body2" color="error" mt={3}>
                                {verifyApiError}
                            </Typography>
                        )}
                    </>
                )}
                {otpSent && (
                    <Button
                        variant="contained"
                        onClick={handleVerifyOtp}
                        sx={{
                            backgroundColor: "#3ea787",
                            mt: 4,
                            width: "90%",
                            maxWidth: 350,
                            height: 35,
                        }}
                    >
                        Verify OTP
                    </Button>
                )}
            </Box>
        </>
    )
}

export default MobileOtpVerification
